// BatteryStorageCard — Phase 3 of the Site Overview redesign (djin-cloud#67).
//
// Row 3 right panel on the dashboard grid (reordered per #273; was row 4 left
// pre-2026-05-11). Shows BESS state at a glance:
//   - Primary SOC tile (large, muted ring when null).
//   - Active power (signed kW — sign encodes direction).
//   - Available energy, nameplate (static), state of health, online N / M.
//
// Design contract: docs/plans/2026-04-20-site-overview-redesign-design.md §4.2.
// Mockup: docs/plans/2026-04-20-site-overview-mockup.html (row 4 left in mockup; row 3 right after #273).
//
// Null-safety: any missing value renders as "—". State of Health tile is
// hidden entirely when null (not rendered with an em-dash — that's the design
// decision in §4.2 to reduce tile count when the data simply isn't available).
//
// Workspace rules:
//   - [No fabricated fallback data]: no `?? 50` / `?? 95` style defaults.
//     Null stays null and renders as "—" or hides the tile.

import type { KPIData } from '../../../types/project.types';

export interface BatteryStorageCardProps {
  /** Cold-load asset KPIs from `/api/v1/projects/:id` `data.kpis`. */
  kpis: KPIData[];
  /** Static nameplate capacity from `site.kpi_metadata.battery_storage.nameplate_capacity_mwh`. */
  nameplateCapacityMwh: number | null;
  /** Live site_summary fields from the WebSocket telemetry:batch payload. */
  kpiValues: Record<string, number | null>;
}

function findKpi(kpis: KPIData[], name: string): number | null {
  const match = kpis.find((k) => k.asset_type === 'battery_storage' && k.kpi_name === name);
  return match ? match.kpi_value : null;
}

function formatNumber(val: number | null, fractionDigits = 1): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

function formatSignedPower(val: number | null): string {
  if (val == null) return '—';
  const abs = Math.abs(val).toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (val > 0) return `+${abs}`;
  if (val < 0) return `−${abs}`;
  return '0';
}

function formatCount(online: number | null, total: number | null): string {
  if (online == null || total == null) return '— / —';
  return `${online} / ${total}`;
}

export function BatteryStorageCard({ kpis, nameplateCapacityMwh, kpiValues }: BatteryStorageCardProps) {
  const soc = findKpi(kpis, 'state_of_charge');
  const activePowerKw = findKpi(kpis, 'active_power_kw');
  const soh = findKpi(kpis, 'state_of_health');
  // BMS comms-loss indicator (djin-cloud#559 / genieos Demsv1.0#1511). bms_online
  // is a 0/1 edge KPI; 0 means the site cannot reach the BMS over Modbus, so
  // SOC/cell/pack data is stale — NOT a genuine SOC = 0. Absent (null) → not
  // applicable, render the normal "Live" state (tri-state, no fabrication).
  const bmsCommsDown = findKpi(kpis, 'bms_online') === 0;
  const available = kpiValues.bess_available_kwh ?? null;
  const online = kpiValues.bess_online_count ?? null;
  const total = kpiValues.bess_total_count ?? null;

  // Muted ring when SOC unknown — no fabricated ring value.
  const socPct = soc == null ? null : Math.max(0, Math.min(100, soc));
  const ringColor = socPct == null
    ? 'stroke-ems-border'
    : socPct >= 60
      ? 'stroke-ems-green'
      : socPct >= 20
        ? 'stroke-ems-amber'
        : 'stroke-ems-red';

  // SVG ring geometry: r = 28, stroke-width = 6, circumference ≈ 175.93.
  const circumference = 2 * Math.PI * 28;
  const dashOffset = socPct == null ? circumference : circumference * (1 - socPct / 100);

  return (
    <section className="card rounded-xl border border-ems-border-bright/50 overflow-hidden bg-ems-bg-card/60 h-full flex flex-col">
      <div className="px-5 py-3 flex items-center gap-2 border-b border-ems-border">
        <h3 className="text-sm font-semibold text-ems-text-primary tracking-wide uppercase">
          Battery Storage
        </h3>
        <div className="ml-auto flex items-center gap-1.5">
          {bmsCommsDown ? (
            <span
              data-testid="bess-bms-comms-down"
              role="status"
              className="inline-flex items-center gap-1 rounded-full border border-ems-red/40 bg-ems-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-ems-red"
              title="The site cannot reach the BMS over Modbus — battery data is stale, not a real SOC of 0."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-ems-red" aria-hidden="true" />
              BMS Comms Down
            </span>
          ) : (
            <>
              <span
                className="w-1.5 h-1.5 rounded-full bg-ems-green animate-ems-pulse"
                aria-hidden="true"
              />
              <span className="text-[10px] text-ems-text-muted uppercase tracking-wider">Live</span>
            </>
          )}
        </div>
      </div>

      <div className="p-4 flex flex-col gap-4 flex-1">
        {/* Primary SOC tile */}
        <div className="flex items-center gap-4">
          <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
            <circle cx="36" cy="36" r="28" strokeWidth="6" className="stroke-ems-border fill-none" />
            <circle
              cx="36"
              cy="36"
              r="28"
              strokeWidth="6"
              className={`${ringColor} fill-none transition-all`}
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
          <div>
            <div className="text-[10px] text-ems-text-muted uppercase tracking-wider mb-1">State of Charge</div>
            <div className="flex items-baseline gap-1">
              <span data-testid="bess-soc-value" className="text-3xl font-mono font-bold tabular-nums text-ems-text-primary">
                {formatNumber(soc, 0)}
              </span>
              {soc != null && <span className="text-sm text-ems-text-muted">%</span>}
            </div>
          </div>
        </div>

        {/* Secondary grid — 2 rows × {3..} tiles; SOH is conditional */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Tile
            label="Active Power"
            testId="bess-active-power-value"
            value={formatSignedPower(activePowerKw)}
            unit="kW"
            valueClassName={
              activePowerKw == null
                ? undefined
                : activePowerKw > 0
                  ? 'text-ems-green'
                  : activePowerKw < 0
                    ? 'text-ems-blue'
                    : undefined
            }
          />
          <Tile
            label="Available Energy"
            testId="bess-available-energy-value"
            value={formatNumber(available)}
            unit="kWh"
          />
          <Tile
            label="Nameplate"
            testId="bess-nameplate-value"
            value={
              nameplateCapacityMwh == null
                ? '—'
                : nameplateCapacityMwh.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
            }
            unit="MWh"
          />
          {soh != null && (
            <Tile
              label="State of Health"
              testId="bess-soh-value"
              value={formatNumber(soh, 0)}
              unit="%"
              valueClassName={soh >= 90 ? 'text-ems-green' : soh >= 80 ? 'text-ems-amber' : 'text-ems-red'}
            />
          )}
          <Tile
            label="Online"
            testId="bess-online-value"
            value={formatCount(online, total)}
          />
        </div>
      </div>
    </section>
  );
}

interface TileProps {
  label: string;
  testId?: string;
  value: string;
  unit?: string;
  valueClassName?: string;
}

function Tile({ label, testId, value, unit, valueClassName }: TileProps) {
  return (
    <div className="rounded-lg p-3 bg-ems-bg-card/40 border border-ems-border">
      <div className="text-[10px] text-ems-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-baseline gap-1">
        <span
          data-testid={testId}
          className={`text-xl font-mono font-bold tabular-nums ${valueClassName ?? 'text-ems-text-primary'}`}
        >
          {value}
        </span>
        {unit && <span className="text-xs text-ems-text-muted">{unit}</span>}
      </div>
    </div>
  );
}

export default BatteryStorageCard;
